import { useState } from "react";
import { ConceptRefinementPanel } from "@/components/concepts/ConceptRefinementPanel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCreateConcept, useUpdateConcept } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUpdateFacet } from "@/hooks/useFacets";
import { type AngleDraft, useDraftAngle } from "@/hooks/useSetup";
import { plainText } from "@/lib/conceptGuidance";
import { toast } from "@/lib/toast";
import { eg, pickExample } from "@/lib/setup/examples";
import { useSetupStore } from "@/stores/setup-store";
import type { SeedCandidate } from "@/stores/seed-discovery-store";
import type { Concept } from "@/types/concept";
import { AngleCard } from "./AngleCard";
import { ConceptSoFar } from "./ConceptSoFar";
import { httpError } from "./http";
import { LoadingLine } from "./LoadingLine";
import { type PickedPaper, PaperSearchBox } from "./PaperSearchBox";

export function toAnchorCandidate(p: PickedPaper): SeedCandidate {
  return {
    paper_id: p.paperId ?? "", title: p.title, authors: (p.authors ?? []).map((a) => a.name),
    year: p.year ?? null, abstract: p.abstract ?? null, citation_count: p.citationCount ?? 0,
    doi: p.externalIds?.DOI ?? null, overlap_score: 0, reason: "Your starting paper",
    journal: p.venue || null,
  };
}

/** Angle labels are capped at 80 (FacetCreate / ConceptCreate.angle_label). */
const ANGLE_LABEL_MAX = 80;
const cap = (s: string) => s.slice(0, ANGLE_LABEL_MAX);

export function AngleStep({ concept, onDone }: {
  concept?: Concept;
  onDone: (conceptId: string, entry: "describe" | "paper") => void;
}) {
  const { data: me } = useCurrentUser();
  const facets = !!me?.features?.facets;
  const primary = concept?.facet_list?.find((f) => f.position === 0) ?? concept?.facet_list?.[0];
  const [example] = useState(() => pickExample());
  const [name, setName] = useState(concept?.name ?? "");
  const [mode, setMode] = useState<"describe" | "paper">("describe");
  const [text, setText] = useState("");
  const [draft, setDraft] = useState<AngleDraft | null>(
    concept ? { label: cap(primary?.label ?? concept.name), question: plainText(concept.abstract), search_terms: concept.keywords } : null,
  );
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const anchor = useSetupStore((s) => s.anchor);
  const draftAngle = useDraftAngle();
  const createConcept = useCreateConcept();
  const updateConcept = useUpdateConcept(concept?.id ?? "");
  const updateFacet = useUpdateFacet(concept?.id ?? "");

  // Non-facets users have one name: the card's label pill edits the concept name.
  const cardLabel = facets ? (draft?.label ?? "") : name;
  const setCardLabel = (v: string) => (facets ? setDraft((d) => (d ? { ...d, label: v } : d)) : setName(v));

  async function runDraft(body: Parameters<typeof draftAngle.mutateAsync>[0]) {
    setError(null);
    try {
      const d = await draftAngle.mutateAsync(body);
      setDraft({ ...d, label: cap(d.label) });
      // Show the suggested name so it can be seen and edited; never replace a typed one.
      setName((n) => (n.trim() ? n : d.label));
    } catch (err) {
      const { status, detail } = await httpError(err);
      if (status === 422 && detail === "no_abstract") {
        setMode("describe");
        setError("We couldn't find an abstract for that paper. Describe your angle in a sentence or two instead; we'll still use the paper as a seed.");
      } else {
        setError("Couldn't draft that. Write it in your own words below, or try again.");
      }
    }
  }

  function pickPaper(p: PickedPaper) {
    useSetupStore.getState().setAnchor(toAnchorCandidate(p), p.fieldsOfStudy ?? null);
    void runDraft({
      name, paper: { title: p.title, year: p.year, abstract: p.abstract, tldr: p.tldr?.text ?? null, doi: p.externalIds?.DOI ?? null },
    });
  }

  async function next() {
    if (!draft?.question.trim()) return;
    const finalName = name.trim() || draft.label.trim();
    if (!finalName) { setError("Give this concept a name."); return; }
    // Empty label: send none and the server names the angle after the concept
    // (name allows 200 chars, angle_label only 80).
    const angleLabel = draft.label.trim();
    setError(null);
    try {
      let saved: Concept;
      if (concept) {
        saved = await updateConcept.mutateAsync({ name: finalName, abstract: draft.question, keywords: draft.search_terms });
        // Compare with the saved angle, not the stale prop: a rename moves a
        // single angle's label along when it equalled the old concept name.
        const savedPrimary = saved.facet_list?.find((f) => f.position === 0) ?? saved.facet_list?.[0];
        const savedLabel = savedPrimary?.label ?? primary?.label;
        if (facets && primary && angleLabel && angleLabel !== savedLabel) {
          try {
            await updateFacet.mutateAsync({ facetId: primary.id, label: angleLabel });
          } catch {
            // The concept is already saved; only the angle rename failed (e.g. a
            // 409 single_facet from a backend that predates single-angle renames).
            // Don't strand the user on ①: say so and move on.
            toast.warning("Couldn't rename the angle", {
              description: "Your concept was saved. You can rename the angle later in Curate.",
            });
          }
        }
      } else {
        saved = await createConcept.mutateAsync({
          name: finalName, abstract: draft.question, keywords: draft.search_terms,
          setup_v2: true, setup_source: anchor ? "from_paper" : "setup",
          ...(facets && angleLabel ? { angle_label: angleLabel } : {}),
        });
      }
      onDone(saved.id, anchor ? "paper" : "describe");
    } catch (err) {
      const { status } = await httpError(err);
      setError(
        status === 403 ? "You've reached your plan's concept limit."
          : status === 409 ? "You already have a concept with that name. Pick another."
          : "Couldn't save. Try again.",
      );
    }
  }

  const busy = createConcept.isPending || updateConcept.isPending || updateFacet.isPending;

  return (
    <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_320px]">
      <section className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold">What are you trying to find out?</h1>
          <p className="mt-1 text-muted-foreground">
            A sentence or two is enough. Every paper gets graded 0–100 against the question this becomes.
          </p>
        </div>

        {mode === "describe" ? (
          <div className="flex flex-col gap-3">
            <label htmlFor="setup-text" className="sr-only">Describe your research</label>
            <Textarea id="setup-text" rows={4} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={eg(example.description)} />
            <div className="flex flex-wrap items-center gap-2">
              <Button disabled={!text.trim() || draftAngle.isPending} onClick={() => runDraft({ name, text })}>
                {draftAngle.isPending ? "Drafting…" : draft ? "Redraft" : "Draft my angle"}
              </Button>
              <Button variant="outline" disabled={!text.trim()}
                onClick={() => setDraft({ label: facets ? "" : cap(name), question: text, search_terms: "" })}>
                Use my words
              </Button>
              <Button variant="link" onClick={() => setMode("paper")}>Start from a paper instead</Button>
              <Button variant="link" className="text-muted-foreground" disabled={!text.trim()} onClick={() => setRefining(true)}>
                Help me refine
              </Button>
            </div>
            {draftAngle.isPending && <LoadingLine>Drafting your angle…</LoadingLine>}
            {refining && (
              <ConceptRefinementPanel
                initialName={name}
                initialDescription={text}
                onSelect={(s) => {
                  setDraft({ label: cap(s.name), question: s.abstract, search_terms: s.keywords.join(", ") });
                  setName((n) => (n.trim() ? n : s.name));
                  setRefining(false);
                }}
                onBack={() => setRefining(false)}
                onCreateAnyway={() => { setDraft({ label: cap(name), question: text, search_terms: "" }); setRefining(false); }}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <PaperSearchBox onPick={pickPaper} />
            {anchor && <p>Starting from <span className="font-medium">{anchor.title}</span></p>}
            {anchor && draftAngle.isPending && <LoadingLine>Drafting an angle from it…</LoadingLine>}
            <Button variant="link" className="w-fit px-0" onClick={() => setMode("describe")}>Describe it instead</Button>
          </div>
        )}

        {draft && (
          <AngleCard
            label={cardLabel} onLabel={setCardLabel}
            question={draft.question} onQuestion={(v) => setDraft({ ...draft, question: v })}
            terms={draft.search_terms} onTerms={(v) => setDraft({ ...draft, search_terms: v })}
            facets={facets} example={example}
          />
        )}

        {error && <p role="alert" className="text-destructive">{error}</p>}

        {draft && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <span className="text-muted-foreground">Next we'll find seed papers for this angle</span>
            <Button disabled={!draft.question.trim() || busy} onClick={next}>Find seeds →</Button>
          </div>
        )}
      </section>

      <ConceptSoFar name={name || draft?.label || ""} onRename={setName}
        angleLabel={facets ? (draft?.label ?? "") : null} scope={concept?.scope} />
    </div>
  );
}
